import { lazy, type FC } from 'react';
import { useParams } from 'react-router-dom';
import { useAuction } from 'src/apis/transport-auction/use-auction';
import { useBalancingGroups } from 'src/apis/transport-auction/use-balancing-groups';
import { usePortfolioCodes } from 'src/apis/transport-auction/use-portfolio-codes';
import {
  useTransportAuctionServiceRoot,
  type OptionalTransportAuctionServiceRootRequest,
} from 'src/apis/transport-auction/use-transport-auction-service-root';
import { useUpgradeableDealIds } from 'src/apis/transport-auction/use-upgradeable-deal-ids';
import {
  PageSpinner,
  SpinnerContainer,
} from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';

const Page = lazy(() => import('./page'));

export const AuctionTAS: FC = () => {
  const transportAuctionServiceRoot = useTransportAuctionServiceRoot();

  if (!transportAuctionServiceRoot.response) return <PageSpinner />;

  return <LoadData transportAuctionServiceRoot={transportAuctionServiceRoot} />;
};

const LoadData: FC<{
  transportAuctionServiceRoot: Successful<OptionalTransportAuctionServiceRootRequest>;
}> = ({ transportAuctionServiceRoot }) => {
  const { tileId, auctionId } = useParams<{
    tileId: string;
    auctionId: string;
  }>();

  const auction = useAuction({
    root: transportAuctionServiceRoot,
    tileId,
    auctionId,
  });

  const entryBalancingGroups = useBalancingGroups(
    auction.response?.data.bidding?.entry?._links?.assignedBalancingGroups
  );
  const exitBalancingGroups = useBalancingGroups(
    auction.response?.data.bidding?.exit?._links?.assignedBalancingGroups
  );
  const entryPortfolioCodes = usePortfolioCodes(
    auction.response?.data.bidding?.entry?._links?.assignedPortfolioCodes
  );
  const exitPortfolioCodes = usePortfolioCodes(
    auction.response?.data.bidding?.exit?._links?.assignedPortfolioCodes
  );
  const entryUpgradeableDealIds = useUpgradeableDealIds(
    auction.response?.data.bidding?.entry?._links?.upgradeableDealIds
  );
  const exitUpgradeableDealIds = useUpgradeableDealIds(
    auction.response?.data.bidding?.exit?._links?.upgradeableDealIds
  );
  const bothUpgradeableDealIds = useUpgradeableDealIds(
    auction.response?.data.bidding?._links?.upgradeableBundledDealIds
  );

  if (!auction.response) return <PageSpinner />;

  return (
    <SpinnerContainer pending={auction.pending}>
      <Page
        auction={auction}
        entryBalancingGroups={entryBalancingGroups}
        exitBalancingGroups={exitBalancingGroups}
        entryPortfolioCodes={entryPortfolioCodes}
        exitPortfolioCodes={exitPortfolioCodes}
        entryUpgradeableDealIds={entryUpgradeableDealIds}
        exitUpgradeableDealIds={exitUpgradeableDealIds}
        bothUpgradeableDealIds={bothUpgradeableDealIds}
      />
    </SpinnerContainer>
  );
};
